import { Link, Stack, Typography } from "@mui/material";
import type { BigDecimal } from "adl-gen/common";
import {
  makeEmpty,
  type Product,
  type UnitType,
} from "adl-gen/ferovinum/app/db";
import {
  FilePicker,
  RemovableFileItem,
} from "components/library/widgets/file-picker";
import {
  GridTable,
  GridTableColumnFactory,
} from "components/widgets/grid-table/grid-table";
import type { FormikProps } from "formik";
import { useCallback, useMemo } from "react";
import type {
  AllocationWithProducts,
  SetupNewAllocationFormValues,
} from "../new-purchaser-allocation-page-view";
import { CardWithTitle } from "./card-with-title";

interface ProductsRowData {
  id?: string | number;
  productCode: string;
  productName: string;
  quantity: number;
  price: string;
}

export const StockAllocationCard = ({
  form,
  setSelectedFile,
  selectedFile,
}: {
  form: FormikProps<SetupNewAllocationFormValues>;
  setSelectedFile: (file: File | undefined) => void;
  selectedFile: File | undefined;
}) => {
  const onFileUpload = useCallback(
    (file: File) => {
      setSelectedFile(file);

      // Mock a file upload by adding a new list of allocations
      const getRandomProduct = (): Product => {
        const codes = [
          "P-zBH9oOqvG8j",
          "P-FKnJPuFBFoF",
          "P-f4OApD4o530",
          "P-Bl9seqq1m1j",
        ];

        return {
          code: codes[Math.floor(Math.random() * codes.length)],
          name: `Demo Product ${Math.floor(Math.random() * 100)}`,
          producerName: "Producer Name",
          productDate: {
            kind: "nonVintage",
          },
          vesselSize: null,
          unitType: "bottle" as UnitType,
          countryOfOrigin: "GB",
          regionOrigin: "Scotland",
          alcoholByVolumePc: 40,
          productType: "fortified",
          alcoholDetail: {
            kind: "unknown",
            value: makeEmpty(""),
          },
          vesselType: {
            kind: "unknown",
            value: makeEmpty(""),
          },
          ownerId: "O-nEsut19N2cemhL66xS6z",
          updatedAt: 1725880060,
          batchRequired: false,
        };
      };

      const allocationsToAdd: AllocationWithProducts[] = [
        {
          product: {
            id: "P-H3livv2nZYf",
            value: getRandomProduct(),
          },
          allocationData: {
            productId: "P-H3livv2nZYf",
            price: "100.0" as BigDecimal,
            remainingQuantity: 100,
          },
        },
        {
          product: {
            id: "P-Rzr2d2sMFjR",
            value: getRandomProduct(),
          },
          allocationData: {
            productId: "P-Rzr2d2sMFjR",
            price: "150.0" as BigDecimal,
            remainingQuantity: 200,
          },
        },
      ];

      form.setFieldValue("allocationsWithProducts", allocationsToAdd);
    },
    [form, setSelectedFile],
  );

  const factory = new GridTableColumnFactory<ProductsRowData>();
  const cols = [
    factory.makeTextColumn({
      header: "Product Code",
      name: "productCode",
      getter: (row) => row.productCode,
    }),
    factory.makeTextColumn({
      header: "Product Name",
      name: "productId",
      getter: (row) => row.productName,
    }),
    factory.makeNumberColumn({
      key: "quantity",
      header: "Quantity",
      displayFormatter: (row) => row.quantity,
    }),
    factory.makeTextColumn({
      header: "Sale Price",
      name: "price",
      getter: (row) => row.price,
    }),
  ];

  const productRowData: ProductsRowData[] = useMemo(
    () =>
      form.values.allocationsWithProducts.map(
        ({ allocationData, product }) => ({
          productCode: product?.value.code ?? "",
          productName: product?.value.name ?? "",
          quantity: allocationData.remainingQuantity,
          price: allocationData.price.toString(),
        }),
      ),
    [form.values.allocationsWithProducts],
  );

  return (
    <CardWithTitle
      title="Stock Allocation"
      subtitle={
        <Stack direction="row" spacing={0.75}>
          <Typography variant="body1">Please populate</Typography>
          <Link display="inline">this template,</Link>
          <Typography variant="body1">
            to specify the stock allocated to the selected purchaser(s). Once
            populated, please upload below.
          </Typography>
        </Stack>
      }
      body={
        <>
          {/* Checking if its a single allocation edit here because if they choose to edit multiple we should not show an allocation list */}
          {form.values.allocationsWithProducts.length && (
            <Stack>
              <Typography>Currently Active:</Typography>
              <GridTable
                tableHeight="auto"
                columns={cols}
                inputRowsField={productRowData}
                rowHeight={30}
              />
            </Stack>
          )}
          <Stack spacing={4} width={"100%"}>
            <FilePicker
              accept="text/csv"
              onFilesAccepted={(files) => onFileUpload(files[0])}
              addExtraPadding
            />
            {selectedFile && (
              <RemovableFileItem
                file={selectedFile}
                onClick={() => setSelectedFile(undefined)}
              />
            )}
          </Stack>
        </>
      }
    />
  );
};
